import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { attachmentStorageKey, newId, type IncidentDetail, type IncidentLine, type IncidentPhoto, type IncidentRegister } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import {
  INCIDENT_CLOSURE_MAX, INCIDENT_NOTE_MAX, SEVERITY_TONE, actionProblem, buildActionPayload, buildClosePayload, closeProblem, followUpJobs, isActionOpen, kindText,
  openActions, registerSections, responsibleOptions, severityText, statusText, yearRange, type ActionProblem, type CloseProblem,
} from '../data/incidents.ts'
import { useMemberships, usePersons } from '../data/people.ts'
import { dayText } from '../data/staff.ts'
import { say, useJobAreas, useJobs } from '../data/plan.ts'
import { machineLabel } from '../data/planUi.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { BackLink, Banner, DisabledHint, Field, NotAllowed, Pill } from '../ui/components.tsx'
import { StepUpField, useSensitive, type Sensitive } from '../ui/sensitive.tsx'
import { useAreaOptions, usePersonNames } from './measures/shared.tsx'
import { TwoTap } from './workshop/shared.tsx'

/** Réglages › Registre des incidents (benchmark 63, GK-044; docs/M16.md G): the direction's alone (incident.manage), read ONLINE after a fresh second-factor code
 *  through the audited `api.read_incidents` (the register: never the text) and `api.read_incident` (one incident whole, one more recorded read), and kept only in
 *  this page's state: nothing of it is mirrored, cached or left in the outbox once answered (sync/transient.ts), and the photos are fetched with `no-store` into
 *  object URLs released when the page lets go of them (ADR-119). Giving it to someone, an action (an ordinary job of the plan, rule 1, and/or a note) and the
 *  closure are Y* commands (useSensitive().command: nonce, the server's verdict); the page reads again after each (M5). */
export function IncidentsPage() {
  const { t } = useTranslation()
  const { can } = useClub()
  const back = <BackLink to="/settings">← {t('settings.title')}</BackLink>
  if (!can('incident.manage')) return <section className="flex flex-col gap-4" data-testid="incidents-page">{back}<h1 className="text-2xl font-bold">{t('incidents.register.title')}</h1><NotAllowed permission="incident.manage" /></section>
  return <Register back={back} />
}

function Register({ back }: { back: ReactNode }) {
  const { t } = useTranslation()
  const { org, site, deviceId } = useClub()
  const s = useSensitive()
  const thisYear = Number(localDate(new Date(), site.timezone).slice(0, 4))
  const [year, setYear] = useState(thisYear)
  const [asked, setAsked] = useState(false)
  const [reg, setReg] = useState<IncidentRegister | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  // release b review (finding 8): back from a follow-up job made with "Créer un travail" (`?open=<incident>&job=<job>`): the register is read again (the code is
  // asked only when the last proof is too old) and the incident opens with that job chosen in "Ajouter une suite"
  const back_ = useSearch({ strict: false }) as { open?: string; job?: string }
  const load = async (y: number): Promise<boolean> => {
    setAsked(true)
    const { from, to } = yearRange(y)
    const r = await s.call<IncidentRegister>('read_incidents', { p_org: org.org_id, p_site: site.site_id, p_from: from, p_to: to, p_device_id: deviceId })
    if (r) { setReg(r); setYear(y); if (back_.open && r.incidents.some((l) => l.id === back_.open)) setOpenId(back_.open) }
    return !!r
  }
  useEffect(() => { if (back_.open && s.fresh && !reg && !asked) void load(thisYear) }, [back_.open])    // eslint-disable-line react-hooks/exhaustive-deps
  const sections = reg ? registerSections(reg.incidents) : null
  return (
    <section className="flex flex-col gap-4" data-testid="incidents-page">
      {back}
      <div>
        <h1 className="text-2xl font-bold">{t('incidents.register.title')}</h1>
        <p className="meta">{t('incidents.register.help')}</p>
      </div>
      <Banner tone="warn" testId="incidents-register-line">{t('incidents.report.registerLine')}</Banner>
      {!openId && <StepUpField s={s} />}
      {!reg || !sections ? (
        <button className="btn btn-primary self-start" disabled={s.busy || !s.online} onClick={() => void load(year)} data-testid="incidents-show">{t('incidents.register.show')}</button>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="incidents-year" data-year={year}>
            <button type="button" className="btn" disabled={s.busy} onClick={() => void load(year - 1)} data-testid="incidents-year-prev">‹ {year - 1}</button>
            <span className="px-2 text-lg font-bold">{year}</span>
            {year < thisYear && <button type="button" className="btn" disabled={s.busy} onClick={() => void load(year + 1)} data-testid="incidents-year-next">{year + 1} ›</button>}
          </div>
          <div className="flex flex-col gap-2" data-testid="incidents-open">
            <h2 className="text-lg font-bold">{t('incidents.register.openTitle', { count: sections.open.length })}</h2>
            {sections.open.length === 0 && <p className="meta" data-testid="incidents-open-none">{t('incidents.register.openNone')}</p>}
            {sections.open.map((l) => <IncidentCard key={l.id} line={l} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)} s={s} onChanged={async () => { await load(year) }} followUpJob={l.id === back_.open ? back_.job : undefined} />)}
          </div>
          <div className="flex flex-col gap-2" data-testid="incidents-closed">
            <h2 className="text-lg font-bold">{t('incidents.register.closedTitle', { year, count: sections.closed.length })}</h2>
            {sections.closed.length === 0 && <p className="meta">{t('incidents.register.closedNone')}</p>}
            {sections.closed.map((l) => <IncidentCard key={l.id} line={l} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)} s={s} onChanged={async () => { await load(year) }} />)}
          </div>
          <button type="button" className="btn self-start" onClick={() => { setReg(null); setOpenId(null); setAsked(false); s.reset() }} data-testid="incidents-hide">{t('incidents.register.hide')}</button>
        </>
      )}
    </section>
  )
}

/** One line of the register; opened, the incident whole (one more recorded read). */
function IncidentCard({ line, open, onToggle, s, onChanged, followUpJob }: { line: IncidentLine; open: boolean; onToggle: () => void; s: Sensitive; onChanged: () => Promise<void>; followUpJob?: string }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const nameOf = usePersonNames()
  const place = usePlaceText(line.area_id, line.asset_id)
  return (
    <div className="card flex flex-col gap-2" data-testid="incident-line" data-id={line.id} data-status={line.status}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-semibold">{kindText(t, line.kind)}</span>
        <Pill tone={SEVERITY_TONE[line.severity]}>{severityText(t, line.severity)}</Pill>
        <Pill tone={line.status === 'closed' ? 'green' : 'amber'}>{statusText(t, line.status)}</Pill>
        {line.attribution_unverified && <Pill tone="amber"><span data-testid="incident-unverified">{t('incidents.register.unverified')}</span></Pill>}
      </div>
      <p className="meta">{formatLocal(line.occurred_at, site.timezone)}{place ? ` · ${place}` : ''}</p>
      <p className="text-sm">{t('incidents.register.reportedBy', { who: nameOf(line.reported_by_person_id) ?? t('incidents.register.someone') })}
        {' · '}{line.responsible_person_id ? t('incidents.register.responsible', { who: nameOf(line.responsible_person_id) ?? t('incidents.register.someone') }) : t('incidents.register.nobody')}</p>
      <p className="meta" data-testid="incident-counts">{t('incidents.register.actions', { count: line.actions })}{line.actions_open > 0 ? ` · ${t('incidents.register.actionsOpen', { count: line.actions_open })}` : ''} · {t('incidents.register.photos', { count: line.photos })}</p>
      <button type="button" className="btn !px-4 self-start" onClick={onToggle} aria-expanded={open} data-testid="incident-open">{open ? t('incidents.register.close') : t('incidents.register.open')}</button>
      {open && <IncidentPanel id={line.id} s={s} onChanged={onChanged} followUpJob={followUpJob} />}
    </div>
  )
}

/** The area and the machine of an incident, from the mirror (the course and the fleet are on every device). */
function usePlaceText(areaId: string | null, assetId: string | null): string {
  const areas = useAreaOptions(); const assets = useAssets()
  const area = areaId ? areas?.find((a) => a.id === areaId)?.label : null
  const asset = assetId ? assets?.find((a) => a.id === assetId) : undefined
  return [area, asset ? machineLabel(asset) : null].filter(Boolean).join(' · ')
}

function IncidentPanel({ id, s, onChanged, followUpJob }: { id: string; s: Sensitive; onChanged: () => Promise<void>; followUpJob?: string }) {
  const { t, i18n } = useTranslation()
  const { org, site, deviceId } = useClub()
  const nameOf = usePersonNames()
  const [d, setD] = useState<IncidentDetail | null>(null)
  const read = async () => { const r = await s.call<IncidentDetail>('read_incident', { p_org: org.org_id, p_id: id, p_device_id: deviceId }); if (r) setD(r) }
  useEffect(() => { void read() }, [id])                                                   // eslint-disable-line react-hooks/exhaustive-deps
  // M5: read again after a change (the incident, then the register); the reads clear the command's message, so it is said again once they are done
  const after = (doneKey: string) => async (ok: boolean | undefined) => { if (ok) { await read(); await onChanged(); s.setOk(t(doneKey)) } }
  const place = usePlaceText(d?.area_id ?? null, d?.asset_id ?? null)
  // the proof may have expired since the register was read: the code is asked here, and the read is asked again
  if (!d) return (
    <div className="flex flex-col gap-3 border-t pt-3" data-testid="incident-panel-loading">
      <StepUpField s={s} />
      {s.error ? <button type="button" className="btn self-start" disabled={s.busy || !s.online} onClick={() => void read()} data-testid="incident-panel-retry">{t('incidents.panel.retry')}</button>
        : <p className="meta">{t('common.loading')}</p>}
    </div>
  )
  const closed = d.status === 'closed'
  return (
    <div className="flex flex-col gap-4 border-t pt-3 dark:border-night-line" data-testid="incident-panel" data-id={d.id}>
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold">{t('incidents.panel.facts')}</h3>
        <p className="whitespace-pre-wrap break-words" data-testid="incident-description-read">{d.description}</p>
        <p className="meta">{formatLocal(d.occurred_at, site.timezone)}{place ? ` · ${place}` : ''}</p>
        {d.involved_person_ids.length > 0 && <p className="text-sm" data-testid="incident-involved">{t('incidents.panel.involved', { names: d.involved_person_ids.map((p) => nameOf(p) ?? t('incidents.register.someone')).join(', ') })}</p>}
        <p className="text-sm">{t('incidents.register.reportedBy', { who: nameOf(d.reported_by_person_id) ?? t('incidents.register.someone') })} · {t('incidents.panel.entered', { when: formatLocal(d.entered_at, site.timezone) })}</p>
        {d.attribution_unverified && <p className="meta">{t('incidents.panel.unverifiedHelp')}</p>}
      </div>
      {d.photos.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold">{t('incidents.panel.photos')}</h3>
          <div className="flex flex-wrap gap-2" data-testid="incident-photos">{d.photos.map((p) => <EvidencePhoto key={p.link_id} photo={p} />)}</div>
        </div>
      )}
      <Responsible d={d} s={s} closed={closed} onDone={after('incidents.panel.assigned')} />
      <Actions d={d} s={s} closed={closed} lang={i18n.language} onDone={after('incidents.panel.actionAdded')} followUpJob={followUpJob} />
      {closed ? (
        <div className="flex flex-col gap-1" data-testid="incident-closure">
          <h3 className="font-semibold">{t('incidents.panel.closedOn', { when: d.closed_at ? formatLocal(d.closed_at, site.timezone) : '' })}</h3>
          <p className="whitespace-pre-wrap break-words">{d.closure_note}</p>
          {d.open_actions_reason && <p className="meta">{t('incidents.panel.closedWithOpen', { reason: d.open_actions_reason })}</p>}
        </div>
      ) : <CloseForm d={d} s={s} onDone={after('incidents.panel.closedOk')} />}
      {(s.error || s.ok) && <StepUpField s={s} />}
    </div>
  )
}

/** A photo of the incident: fetched with `no-store` through a URL signed for this reader (the second factor), shown from memory, released on the way out. */
function EvidencePhoto({ photo }: { photo: IncidentPhoto }) {
  const { t } = useTranslation()
  const { transport, org } = useClub()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true; let made: string | null = null
    if (!photo.stored) { setFailed(true); return undefined }
    void (async () => {
      try {
        const signed = await transport.attachmentUrl(attachmentStorageKey(org.org_id, photo.site_id, photo.sha256, photo.mime))
        const r = await fetch(signed, { cache: 'no-store', credentials: 'omit' })
        if (!r.ok) throw new Error(String(r.status))
        const blob = await r.blob()
        if (!live) return
        made = URL.createObjectURL(blob); setUrl(made)
      } catch { if (live) setFailed(true) }
    })()
    return () => { live = false; if (made) URL.revokeObjectURL(made) }
  }, [photo.stored, photo.site_id, photo.sha256, photo.mime, transport, org.org_id])
  const cls = 'size-28 rounded-xl border border-slate-200 object-cover dark:border-night-line'
  return url ? <img src={url} alt={t('incidents.panel.photoAlt')} className={cls} data-testid="incident-photo" />
    : <span className={`${cls} flex items-center justify-center bg-slate-100 p-1 text-center text-[13px] text-slate-700 dark:bg-night dark:text-slate-200`} data-testid="incident-photo-placeholder">{failed ? t('photos.unavailable') : t('common.loading')}</span>
}

function Responsible({ d, s, closed, onDone }: { d: IncidentDetail; s: Sensitive; closed: boolean; onDone: (ok: boolean | undefined) => Promise<void> }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const persons = usePersons(); const memberships = useMemberships()
  const options = useMemo(() => responsibleOptions(persons ?? [], memberships ?? [], site.site_id), [persons, memberships, site.site_id])
  const nameOf = usePersonNames()
  const [who, setWho] = useState(d.responsible_person_id ?? '')
  return (
    <div className="flex flex-col gap-2" data-testid="incident-responsible">
      <h3 className="font-semibold">{t('incidents.panel.responsibleTitle')}</h3>
      <p>{d.responsible_person_id ? nameOf(d.responsible_person_id) ?? t('incidents.register.someone') : t('incidents.register.nobody')}</p>
      {!closed && (
        <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (who) void s.command('incident.assign@1', { incident_id: d.id, responsible_person_id: who }, 'incidents.panel.assigned').then(onDone) }}>
          <Field label={t('incidents.panel.giveTo')} htmlFor={`resp-${d.id}`}>
            <select id={`resp-${d.id}`} className="input" value={who} onChange={(e) => setWho(e.target.value)} data-testid="incident-responsible-select">
              <option value="">{t('incidents.panel.pick')}</option>
              {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <button className="btn self-start" disabled={s.busy || !s.online || !who || who === d.responsible_person_id} data-testid="incident-assign">{t('incidents.panel.assign')}</button>
        </form>
      )}
    </div>
  )
}

function Actions({ d, s, closed, lang, onDone, followUpJob }: { d: IncidentDetail; s: Sensitive; closed: boolean; lang: string; onDone: (ok: boolean | undefined) => Promise<void>; followUpJob?: string }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const nameOf = usePersonNames()
  const jobs = useJobs(); const jobAreas = useJobAreas()
  const candidates = useMemo(() => followUpJobs(jobs ?? [], (jobId) => (jobAreas ?? []).filter((ja) => ja.job_id === jobId && !ja.removed_by_command_id).map((ja) => ja.area_id), d,
    (j) => say(j.title, lang) || j.task_type_code), [jobs, jobAreas, d, lang])
  const [jobId, setJobId] = useState(followUpJob ?? '')
  const [note, setNote] = useState('')
  const [dueOn, setDueOn] = useState('')
  const problem: ActionProblem = actionProblem({ jobId, note, dueOn })
  const add = () => void s.command('incident.add_action@1', buildActionPayload(newId(), d.id, { jobId, note, dueOn }), 'incidents.panel.actionAdded')
    .then(async (ok) => { if (ok) { setJobId(''); setNote(''); setDueOn('') } await onDone(ok) })
  return (
    <div className="flex flex-col gap-2" data-testid="incident-actions">
      <h3 className="font-semibold">{t('incidents.panel.actionsTitle')}</h3>
      {d.actions.length === 0 && <p className="meta">{t('incidents.panel.noAction')}</p>}
      <ul className="flex flex-col gap-2">
        {d.actions.map((a) => (
          <li key={a.id} className="rounded-xl bg-slate-100 p-3 dark:bg-night" data-testid="incident-action" data-open={isActionOpen(a) ? '1' : '0'}>
            {a.job_id && (
              <Link to="/plan/job/$jobId" params={{ jobId: a.job_id }} className="flex min-h-14 items-center font-semibold underline" data-testid="incident-action-job">
                {(say(a.job_title, lang) || a.job_task_type_code || t('incidents.panel.job'))}{a.job_date ? ` · ${dayText(a.job_date)}` : ''}{a.job_state ? ` · ${t(`plan.state.${a.job_state}`)}` : ''}
              </Link>
            )}
            {a.note && <p className="whitespace-pre-wrap break-words">{a.note}</p>}
            <p className="meta">{a.due_on ? `${t('incidents.panel.dueOn', { day: dayText(a.due_on) })} · ` : ''}{formatLocal(a.created_at, site.timezone)}{nameOf(a.actor_person_id) ? ` · ${nameOf(a.actor_person_id)}` : ''}</p>
          </li>
        ))}
      </ul>
      {!closed && (
        <details className="card" open={!!followUpJob || undefined}>
          <summary className="flex min-h-14 cursor-pointer items-center font-semibold" data-testid="incident-action-toggle">{t('incidents.panel.addAction')}</summary>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (!problem) add() }}>
            <p className="meta">{t('incidents.panel.actionHelp')}</p>
            {/* release b review (finding 8): the job form opens on the incident's area with a note, and comes back here once the job is saved */}
            <Link to="/plan/new" search={{ ...(d.area_id ? { area: d.area_id } : {}), note: t('incidents.panel.jobNote', { day: dayText(localDate(d.occurred_at, site.timezone)) }), incident: d.id } as never}
              className="btn" data-testid="incident-create-job">{t('incidents.panel.createJob')}</Link>
            <Field label={t('incidents.panel.pickJob')} htmlFor={`job-${d.id}`}>
              <select id={`job-${d.id}`} className="input" value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="incident-action-job-select">
                <option value="">{t('incidents.panel.noJob')}</option>
                {candidates.map((j) => <option key={j.id} value={j.id}>{`${dayText(j.date)} · ${j.title}${j.onArea ? ` · ${t('incidents.panel.onArea')}` : ''}`}</option>)}
              </select>
            </Field>
            <Field label={t('incidents.panel.note')} hint={t('incidents.panel.noteHint', { max: INCIDENT_NOTE_MAX })} htmlFor={`note-${d.id}`}
              error={problem === 'noteLong' ? t('incidents.panel.noteLong', { max: INCIDENT_NOTE_MAX }) : null}>
              <textarea id={`note-${d.id}`} className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="incident-action-note" />
            </Field>
            <Field label={t('incidents.panel.due')} htmlFor={`due-${d.id}`}>
              <input id={`due-${d.id}`} type="date" className="input" value={dueOn} onChange={(e) => setDueOn(e.target.value)} data-testid="incident-action-due" />
            </Field>
            <button className="btn btn-primary self-start" disabled={s.busy || !s.online || !!problem} data-testid="incident-action-add">{t('incidents.panel.addActionGo')}</button>
            <DisabledHint show={problem === 'empty'} testId="incident-action-hint">{t('incidents.panel.actionEmpty')}</DisabledHint>
          </form>
        </details>
      )}
    </div>
  )
}

function CloseForm({ d, s, onDone }: { d: IncidentDetail; s: Sensitive; onDone: (ok: boolean | undefined) => Promise<void> }) {
  const { t } = useTranslation()
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const open = openActions(d)
  const problem: CloseProblem = closeProblem({ note, reason }, d)
  const hint = problem === 'note' ? t('incidents.panel.missing.note') : problem === 'reason' ? t('incidents.panel.missing.reason', { count: open }) : problem === 'noteLong' ? t('incidents.panel.missing.noteLong', { max: INCIDENT_CLOSURE_MAX }) : problem === 'reasonLong' ? t('incidents.panel.noteLong', { max: INCIDENT_NOTE_MAX }) : ''
  return (
    <details className="card" data-testid="incident-close">
      <summary className="flex min-h-14 cursor-pointer items-center font-semibold" data-testid="incident-close-toggle">{t('incidents.panel.closeTitle')}</summary>
      <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
        <p className="meta" data-testid="incident-close-final">{t('incidents.panel.closeFinal')}</p>
        <Field label={t('incidents.panel.closureNote')} hint={t('incidents.panel.closureHint', { max: INCIDENT_CLOSURE_MAX })} htmlFor={`close-${d.id}`}>
          <textarea id={`close-${d.id}`} className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="incident-close-note" />
        </Field>
        {open > 0 && (
          <Field label={t('incidents.panel.openReason', { count: open })} hint={t('incidents.panel.openReasonHint')} htmlFor={`reason-${d.id}`}>
            <textarea id={`reason-${d.id}`} className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="incident-close-reason" />
          </Field>
        )}
        <TwoTap label={t('incidents.panel.closeGo')} confirm={t('incidents.panel.closeConfirm')} danger disabled={s.busy || !s.online || !!problem} className="self-start" testId="incident-close-go"
          onConfirm={() => { if (!problem) void s.command('incident.close@1', buildClosePayload(d.id, { note, reason }, d), 'incidents.panel.closedOk').then(onDone) }} />
        <DisabledHint show={!!problem} testId="incident-close-hint">{hint}</DisabledHint>
      </form>
    </details>
  )
}
